import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  Box,
  Alert,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PaymentMethod, PAYMENT_STATUS_COLORS, PAYMENT_STATUS_LABELS } from '../types/booking';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (paymentData: {
    amount: number;
    method: PaymentMethod;
    transactionId?: string;
    notes?: string;
    balanceCollectionTime?: 'handover' | 'return';
  }) => Promise<void>;
  bookingNumber: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  paymentStatus: 'unpaid' | 'partial' | 'paid' | 'refunded';
  showBalanceTimingOption?: boolean; // New prop for POS New Booking
  isExtension?: boolean; // New prop to indicate this is for extension (only show "return" option)
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  onSubmit,
  bookingNumber,
  totalAmount,
  paidAmount,
  balanceAmount,
  paymentStatus,
  showBalanceTimingOption = false, // Default false for backwards compatibility
  isExtension = false, // Default false
}) => {
  const { t } = useTranslation();
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');
  const [balanceCollectionTime, setBalanceCollectionTime] = useState<'handover' | 'return'>('return');
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  // Pre-fill payment amount with balance when dialog opens
  useEffect(() => {
    if (open) {
      setPaymentAmount(balanceAmount.toFixed(2));
      setTransactionId('');
      setNotes('');
      setError(null);
    }
  }, [open, balanceAmount]);

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(2)} TND`;
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError(t('errorHandling.payment.invalidAmount'));
      return;
    }

    // Round to 2 decimal places to avoid floating-point precision errors
    const roundedAmount = Math.round(amount * 100) / 100;
    const roundedBalance = Math.round(balanceAmount * 100) / 100;

    if (roundedAmount > roundedBalance) {
      setError(t('errorHandling.payment.exceedsBalance', { balance: formatCurrency(balanceAmount) }));
      return;
    }

    if (paymentMethod !== 'cash' && !transactionId.trim()) {
      setError(t('errorHandling.payment.transactionIdRequired'));
      return;
    }

    try {
      setProcessing(true);
      await onSubmit({
        amount: roundedAmount, // Use rounded amount
        method: paymentMethod,
        transactionId: transactionId.trim() || undefined,
        notes: notes.trim() || undefined,
        balanceCollectionTime: showBalanceTimingOption && roundedAmount < roundedBalance
          ? balanceCollectionTime
          : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorHandling.payment.saveFailed'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Enregistrer le paiement
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Booking Summary */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ p: 2, bgcolor: '#F8F9FF', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Reservation : {bookingNumber}
              </Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Montant total
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(totalAmount)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Deja paye
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    {formatCurrency(paidAmount)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Solde du
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="error.main">
                    {formatCurrency(balanceAmount)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      Statut du paiement :
                    </Typography>
                    <Chip
                      label={PAYMENT_STATUS_LABELS[paymentStatus]}
                      size="small"
                      sx={{
                        bgcolor: PAYMENT_STATUS_COLORS[paymentStatus],
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>

          {/* Payment Form */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Details du paiement
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Montant du paiement (TND) *"
              type="number"
              fullWidth
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()} // Prevent scroll change
              onKeyDown={(e) => {
                // Prevent arrow keys from changing value
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault();
                }
              }}
              inputProps={{
                step: '0.01',
                min: '0.01',
                max: balanceAmount,
              }}
              helperText={`Maximum : ${formatCurrency(balanceAmount)}`}
              disabled={processing}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Mode de paiement *</FormLabel>
              <RadioGroup
                row
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
              >
                <FormControlLabel
                  value="cash"
                  control={<Radio />}
                  label="Especes"
                  disabled={processing}
                />
                <FormControlLabel
                  value="credit_card"
                  control={<Radio />}
                  label="Carte de credit"
                  disabled={processing}
                />
                <FormControlLabel
                  value="debit_card"
                  control={<Radio />}
                  label="Carte de debit"
                  disabled={processing}
                />
                <FormControlLabel
                  value="bank_transfer"
                  control={<Radio />}
                  label="Virement bancaire"
                  disabled={processing}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {paymentMethod !== 'cash' && (
            <Grid size={{ xs: 12 }}>
              <TextField
                label="ID de transaction *"
                fullWidth
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="Saisir l ID de transaction ou la reference"
                disabled={processing}
              />
            </Grid>
          )}

          {/* Balance Collection Timing - Only for POS New Booking with partial payment */}
          {showBalanceTimingOption && parseFloat(paymentAmount) > 0 && parseFloat(paymentAmount) < balanceAmount && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ p: 2, bgcolor: '#FFF4E5', borderRadius: 1, border: '1px solid #FF690F' }}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1, color: '#FF690F', fontWeight: 'bold' }}>
                    Quand le solde sera-t-il paye ?
                  </FormLabel>
                  <RadioGroup
                    value={balanceCollectionTime}
                    onChange={(e) => setBalanceCollectionTime(e.target.value as 'handover' | 'return')}
                  >
                    {!isExtension && (
                      <FormControlLabel
                        value="handover"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">Paiement a la remise</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Suivre le solde a encaisser a la remise
                            </Typography>
                          </Box>
                        }
                        disabled={processing}
                      />
                    )}
                    <FormControlLabel
                      value="return"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">Paiement au retour</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Suivre le solde a encaisser au retour
                          </Typography>
                        </Box>
                      }
                      disabled={processing}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Notes de paiement (facultatif)"
              fullWidth
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes supplementaires sur ce paiement..."
              disabled={processing}
            />
          </Grid>

          {error && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {balanceAmount === 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="success" icon={<SuccessIcon />}>
                <strong>La reservation est entierement payee !</strong> Aucun solde restant.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={processing}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={processing || balanceAmount === 0}
          startIcon={processing ? <CircularProgress size={20} /> : <PaymentIcon />}
          sx={{
            bgcolor: '#5EBD81',
            '&:hover': { bgcolor: '#4AA76B' },
          }}
        >
          {processing ? 'Enregistrement...' : 'Enregistrer le paiement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
